import './overrides';

import { Localization as GeneralLocalization } from '@mui/material/locale';
import {
    createTheme,
    Theme,
    ThemeProvider,
    useTheme
} from '@mui/material/styles';
import { Localization as GridLocalization } from '@mui/x-data-grid-pro/internals';
import { useEffect, useState } from 'react';

import { breadcrumbsStyle } from '../breadcrumbs';
import { buttonStyle } from '../button';
import { checkboxStyle } from '../checkbox';
import { chipStyle } from '../chip';
import { dialogStyle } from '../dialog/style';
import { SIDE_NAV_WIDTH } from '../drawer/nav-drawer';
import { formControlStyle } from '../form-control';
import { radioStyle } from '../radio';
import { textfieldStyle } from '../textfield';
import { tooltipStyle } from '../tooltip';
import { deepMerge } from '../utils';
import {
    type ColorProp,
    getPaletteColor,
    usePaletteColor
} from './color-utils';
import { CONTENT_BREAKPOINTS } from './content-breakpoints';
import { FontProvider } from './fonts';
import themes, {
    type ColorToken,
    DefaultColor,
    Palette,
    ThemeName,
    TypographyToken
} from './manifest';
import { useThemeType } from './type';

export * from './content-container';
export * from './content-breakpoints';

export { Palette, DefaultColor, ColorToken, ColorProp };
export { usePaletteColor, getPaletteColor };
export * from './baseline';
export * from './manifest';
export * from './type';
export type { Theme };

export type ThemeType =
    | 'magicline'
    | 'magicline-v2'
    | 'perfectgym'
    | 'planet-fitness'
    | 'perfectleisure';

export type Localization = GeneralLocalization | GridLocalization;

type ThemeOptions = Exclude<Parameters<typeof createTheme>[0], undefined>;

const DEFAULT_FONT_SIZE = 14;

export const DESKTOP_MEDIA_QUERY =
    '@media (min-width: 1024px) and (min-height: 864px)';

const defaultThemeOptions: ThemeOptions = {
    breakpoints: {
        values: {
            xs: 0,
            sm: CONTENT_BREAKPOINTS.sm + SIDE_NAV_WIDTH,
            md: CONTENT_BREAKPOINTS.md + SIDE_NAV_WIDTH,
            lg: CONTENT_BREAKPOINTS.lg + SIDE_NAV_WIDTH,
            xl: CONTENT_BREAKPOINTS.xl + SIDE_NAV_WIDTH
        }
    },
    typography: {
        fontSize: DEFAULT_FONT_SIZE,
        htmlFontSize: DEFAULT_FONT_SIZE
    },
    components: {
        ...buttonStyle,
        ...chipStyle,
        ...textfieldStyle,
        ...formControlStyle,
        ...radioStyle,
        ...checkboxStyle,
        ...tooltipStyle,
        ...breadcrumbsStyle,
        ...dialogStyle
    }
};

export type ThemeMode = 'light' | 'dark';

export function getTheme(
    type: ThemeType = 'magicline',
    mode: ThemeMode = 'light',
    localization?: Localization
) {
    const name = ((): ThemeName => {
        switch (type) {
            case 'magicline':
                return 'magicline';
            case 'magicline-v2':
                return 'magicline-v2';
            case 'perfectgym':
                return mode === 'light'
                    ? 'perfect-gym-next'
                    : 'pgn-light-top-navigation';
            case 'planet-fitness':
                return 'planet-fitness';
            case 'perfectleisure':
                return 'perfect-leisure';
        }
    })();

    const theme = themes[name];

    if (theme.palette) {
        theme.palette.mode = mode;
        theme.palette.components = theme.components;
    }

    return createTheme(
        deepMerge(defaultThemeOptions, themes[name]),
        localization ?? {}
    );
}

const defaultTheme = createTheme(defaultThemeOptions);

export function useRubiksTheme(themeOptions?: {
    type?: ThemeType;
    mode?: ThemeMode;
    localization?: Localization;
}) {
    const themeType = useThemeType();
    const { localization, mode, type = themeType } = themeOptions ?? {};

    const [theme, setTheme] = useState<Theme>(() =>
        type || localization
            ? createTheme(getTheme(type, mode, localization))
            : defaultTheme
    );

    useEffect(() => {
        setTheme(getTheme(type, mode, localization));
    }, [type, mode, localization]);

    return { FontProvider, ThemeProvider, theme };
}

export function useTypographyToken<T extends TypographyToken | undefined>(
    token?: T
): T extends undefined ? undefined : number {
    const theme = useTheme();

    if (!token) {
        return undefined as T extends undefined ? undefined : number;
    }

    interface AnyObject {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    }

    function getValueByPath<V>(obj: AnyObject, path: string): V {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
        return path.split('.').reduce<any>((acc, part) => {
            if (acc && Object.prototype.hasOwnProperty.call(acc, part)) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                return acc?.[part] as V | undefined;
            }

            return undefined;
        }, obj);
    }

    return getValueByPath<number>(theme.palette, token) as T extends undefined
        ? undefined
        : number;
}
